import { useId, useState } from 'react';
import { Text, TypographyStylesProvider } from '@mantine/core';

import Title from '@/components/content/title/Title';

import { sanitize } from '@/utils/sanitize';

import classes from './News.module.css';

interface NewsItemProps {
  date?: string;
  title: string;
  content: string;
}

interface NewsProps {
  newsItem: NewsItemProps;
  headingLevel?: 2 | 3;
  expanded?: boolean;
  layout?: 'cards' | 'updates';
}

const splitUpdateContent = (content: string) => {
  const parsedDocument = new DOMParser().parseFromString(content, 'text/html');
  const nodes = Array.from(parsedDocument.body.childNodes).filter(
    (node) => node.nodeType !== Node.TEXT_NODE || node.textContent?.trim()
  );
  const firstParagraphIndex = nodes.findIndex((node) => node.nodeName.toLowerCase() === 'p');

  const serialize = (selectedNodes: ChildNode[]) => {
    const container = parsedDocument.createElement('div');
    selectedNodes.forEach((node) => container.appendChild(node.cloneNode(true)));
    return container.innerHTML;
  };

  if (firstParagraphIndex === -1 || firstParagraphIndex === nodes.length - 1) {
    return { preview: content, remainder: '' };
  }

  return {
    preview: serialize(nodes.slice(0, firstParagraphIndex + 1)),
    remainder: serialize(nodes.slice(firstParagraphIndex + 1)),
  };
};

const News = ({ newsItem, headingLevel = 3, expanded = false, layout = 'cards' }: NewsProps) => {
  const [isUpdateExpanded, setIsUpdateExpanded] = useState(false);
  const updateContentId = useId();
  const sanitizedContent = sanitize(newsItem.content, {
    ALLOWED_URI_REGEXP: /^(?:(?:mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
  const updateContent = splitUpdateContent(sanitizedContent);

  if (layout === 'updates') {
    return (
      <article className={classes.updateCard}>
        <Title
          order={headingLevel}
          c="var(--ds-font-color)"
          fz={24}
          fw={700}
          lh="32px"
          className={classes.updateTitle}
        >
          <span>{newsItem.title}</span>
        </Title>

        <TypographyStylesProvider
          className={classes.updateContent}
          dangerouslySetInnerHTML={{ __html: updateContent.preview }}
        />

        {updateContent.remainder && (
          <TypographyStylesProvider
            id={updateContentId}
            hidden={!isUpdateExpanded}
            className={`${classes.updateContent} ${classes.updateContentExpanded}`}
            dangerouslySetInnerHTML={{ __html: updateContent.remainder }}
          />
        )}

        <footer className={classes.updateFooter}>
          {newsItem.date && (
            <Text
              component="time"
              dateTime={newsItem.date}
              className={classes.dateChip}
              aria-label={newsItem.date}
            >
              {newsItem.date}
            </Text>
          )}
          {updateContent.remainder && (
            <button
              type="button"
              className={classes.updateButton}
              aria-expanded={isUpdateExpanded}
              aria-controls={updateContentId}
              aria-label={`${isUpdateExpanded ? 'Read less about' : 'Read more about'} ${
                newsItem.title
              }`}
              onClick={() => setIsUpdateExpanded((value) => !value)}
            >
              {isUpdateExpanded ? 'Read less' : 'Read more'}
            </button>
          )}
        </footer>
      </article>
    );
  }

  if (expanded) {
    return (
      <article className={classes.expanded}>
        <header className={classes.header}>
          <Title
            order={headingLevel}
            c="var(--ds-accent-blue-500)"
            fz={24}
            fw={700}
            lh="32px"
            className={classes.title}
          >
            <span>{newsItem.title}</span>
          </Title>
          {newsItem.date && (
            <Text component="time" dateTime={newsItem.date} className={classes.date}>
              {newsItem.date}
            </Text>
          )}
        </header>

        <TypographyStylesProvider
          className={classes.content}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      </article>
    );
  }

  return (
    <div className="elevated-panel elevated-panel--padded">
      <Title
        order={headingLevel}
        style={{ color: 'var(--ds-font-color)', fontSize: '16px', fontWeight: 700 }}
      >
        <span>{newsItem.title}</span>
        {newsItem.date && <Text size="xs">{newsItem.date}</Text>}
      </Title>

      <TypographyStylesProvider mt={10} dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
    </div>
  );
};

export default News;
